import useSWR from "swr";
import { useTranslation } from "react-i18next";
import request from "utils/apiRequestWithErrorCode";

import { ActionType, NotificationVariable } from "@dashboard-v3/api";
import { DashboardVariable } from "types";

const REVALIDATION_OPTIONS = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};

type NotificationType =
  NotificationVariable["rules"][number]["notifications"][number];

type Options = {
  ruleType?: ActionType;
  notificationType?: NotificationType;
};

export default function useNotificationVariables(options?: Options) {
  const { t } = useTranslation("platformVariables");
  const { data } = useSWR(
    "variables/notifications",
    fetcher,
    REVALIDATION_OPTIONS
  );

  let variables = toDashboardVariable(data);

  if (options?.ruleType) {
    const ruleType = options.ruleType;
    variables = variables.filter(v =>
      v.notificationVariableRules?.some(r => r.type.includes(ruleType))
    );
  }

  if (options?.notificationType) {
    const notificationType = options.notificationType;
    variables = variables.filter(v =>
      v.notificationVariableRules?.some(r =>
        r.notifications.includes(notificationType)
      )
    );
  }

  function replaceIdsWithLabels(input: string) {
    if (!input) return "";

    return mapVariables(input, variables, (str, match) => {
      return match ? t(match.name) : str;
    }).join("");
  }

  function replaceLabelsWithIds(label: string) {
    if (!label) return label;

    return label.replace(/\{.*?\}/g, match => {
      const variable = variables.find(s => match === t(s.name));
      return variable ? variable.id : match;
    });
  }

  return { variables, replaceIdsWithLabels, replaceLabelsWithIds };
}

function fetcher() {
  return request<NotificationVariable[]>(
    "GET",
    "/platform/variables/notificationSources"
  );
}

function mapVariables<T>(
  input: string,
  vars: DashboardVariable[],
  handler: (str: string, match: DashboardVariable | undefined) => T
) {
  return input
    .split(/(\{.*?\})/g)
    .filter(str => str)
    .map(str => {
      const match = vars.find(v => str === v.id);
      return handler(str, match);
    }) as T[];
}

function toDashboardVariable(
  variables: NotificationVariable[]
): DashboardVariable[] {
  if (!variables) return [];

  return variables.map(nv => ({
    id: `{${nv.id}}`,
    name: `variables.${nv.id}.name`,
    description: `variables.${nv.id}.description`,
    origin: "PLATFORM" as const,
    notificationVariableRules: nv.rules,
  }));
}
